import React, { useCallback, useState } from 'react';

import { confirmDialog } from '@/utils/dialog';
import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState } from '../types';
import type { CheckInMode } from '../components/mode-toggle';
import { createEmptyEntry, createEmptyNoteEntry, QuickCheckInState } from './use-check-in-form-state';

export interface UseCheckInModeOptions {
  entries: CheckInEntryState[];
  noteEntries: NoteEntryState[];
  setState: React.Dispatch<React.SetStateAction<QuickCheckInState>>;
  setNoteEntries: React.Dispatch<React.SetStateAction<NoteEntryState[]>>;
}

export function useCheckInMode({
  entries,
  noteEntries,
  setState,
  setNoteEntries,
}: UseCheckInModeOptions) {
  const [mode, setMode] = useState<CheckInMode>('check-in');

  const noteEntriesHaveContent = useCallback((): boolean => {
    return noteEntries.some(
      (e) => e.menuItem !== null || e.content.trim().length > 0 || e.photo !== null
    );
  }, [noteEntries]);

  const checkInEntriesHaveContent = useCallback((): boolean => {
    return entries.some(
      (e) =>
        e.menuItem !== null ||
        e.ratingId !== null ||
        e.photos.length > 0 ||
        e.reviewContent.trim().length > 0
    );
  }, [entries]);

  const handleModeChange = useCallback(
    (newMode: CheckInMode) => {
      if (newMode === mode) return;

      const currentHasContent =
        mode === 'check-in' ? checkInEntriesHaveContent() : noteEntriesHaveContent();

      const performSwitch = () => {
        if (mode === 'check-in') {
          setState((prev) => ({
            ...prev,
            entries: [createEmptyEntry()],
          }));
        } else {
          setNoteEntries([createEmptyNoteEntry()]);
        }
        setMode(newMode);
      };

      if (!currentHasContent) {
        setMode(newMode);
        return;
      }

      confirmDialog(
        'Switch Mode?',
        'Switching modes will clear your current entries. Continue?',
        performSwitch,
        () => {},
      );
    },
    [mode, checkInEntriesHaveContent, noteEntriesHaveContent, setState, setNoteEntries]
  );

  return { mode, handleModeChange };
}
